<template>
  <Stack class="ask-side">
    <Stack gap="2">
      <Label>How to use this AI Assistant</Label>
      <ul class="list">
        <li>
          Be clear and specific with your request (e.g. “Summarize my latest meeting in 3 points”).
        </li>
        <li>
          Provide context so it can tailor the answer (what you're working on, audience, tone, etc.).
        </li>
        <li>
          Use follow-ups to refine the output (“make it shorter,” “add examples”).
        </li>
      </ul>
    </Stack>

    <Stack gap="2">
      <Label>Settings</Label>
      <Buttons>
        <Button
          variant="ask-anything"
          icon-before="fas fa-edit"
          size="3_5"
          @click="openPromptModal"
        >
          Customize Prompt
        </Button>
      </Buttons>
    </Stack>

    <Stack gap="2">
      <Label>Disclaimer</Label>
      <p class="ask-disclaimer">
        The AI Assistant may make mistakes. We recommend checking important information.
      </p>
    </Stack>

    <!-- Original modal kept for reference; replaced by simpler modal below
    <FormModal
      v-model="showPromptModal"
      title="Customize the prompt"
      endpoint="/api/v1/ask-anything/prompt/"
      :fields="fields"
    >
      <div class="content mb-0">
        <p>You can customize the AI by providing a <strong>custom prompt</strong>:</p>
        <ol>
          <li><strong>Define the Role:</strong> Tell the assistant who it should be.</li>
          <li><strong>Set the Tone & Style:</strong> Describe how it should talk.</li>
          <li><strong>Give Behavior Rules:</strong> Control what it should or shouldn't do.</li>
          <li><strong>Specify Formatting:</strong> Tell it how to structure responses.</li>
          <li><strong>Add Domain Knowledge or Context:</strong> Provide any info it should always remember for this session.</li>
        </ol>
      </div>
    </FormModal>
    -->

    <StakeModal v-model="showPromptModal" fullwidth>
      <Stack class="stk-p-4">
        <Title>Customize the prompt</Title>

        <div class="content mb-0">
          <p>You can customize the AI by providing a <strong>custom prompt</strong>:</p>
          <ol>
            <li><strong>Set the Tone & Style:</strong> Describe how it should talk.</li>
            <li><strong>Add Domain Knowledge or Context:</strong> Provide any info it should always remember.</li>
            <li><strong>Give Behavior Rules:</strong> Control what it should or shouldn't do.</li>
          </ol>
        </div>

        <Label for="ask-anything-prompt">Prompt</Label>
        <div class="stk-control">
          <textarea
            id="ask-anything-prompt"
            class="stk-input"
            v-model="promptText"
            rows="10"
          ></textarea>
        </div>

        <Buttons justify="between">
          <Buttons>
            <Button variant="success" @click="savePrompt">Save Changes</Button>
          </Buttons>
          <Button variant="danger" @click="showPromptModal = false">Cancel</Button>
        </Buttons>
      </Stack>
    </StakeModal>
  </Stack>
</template>

<script setup>
import { ref } from 'vue';
import { Buttons, Button, FormModal, Label, Stack, StakeModal, Title, TextInput } from '@stakeui';

const showPromptModal = ref(false);
const fields = ref([
  {
    id: 'prompt',
    type: 'text',
    inputType: 'textarea',
    value: '',
  },
]);
const promptText = ref('');

async function openPromptModal() {
  try {
    const res = await fetch('/api/v1/ask-anything/prompt/', { credentials: 'same-origin' });
    if (res.ok) {
      const data = await res.json();
      const txt = String(data?.prompt_text || '');
      fields.value = [{ id: 'prompt', type: 'text', inputType: 'textarea', value: txt }];
      promptText.value = txt;
    }
  } catch (e) {
    // no-op
  } finally {
    if (!promptText.value) {
      // Initialize simple textarea with current value (if present)
      const current = Array.isArray(fields.value) && fields.value[0] ? String(fields.value[0].value || '') : '';
      promptText.value = current;
    }
    showPromptModal.value = true;
  }
}

async function savePrompt() {
  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]')?.value,
    };
    const body = JSON.stringify({ prompt_text: String(promptText.value || '') });
    const res = await fetch('/api/v1/ask-anything/prompt/', { method: 'POST', headers, body, credentials: 'same-origin' });
    if (!res.ok) throw new Error('failed');
    // keep fields in sync for future opens
    fields.value = [{ id: 'prompt', type: 'text', inputType: 'textarea', value: String(promptText.value || '') }];
    showPromptModal.value = false;
  } catch (e) {
    // no-op
  }
}
</script>

<style scoped>
.ask-side {
  --bg: #fcfcfd;
  background-color: var(--bg);
  border-left: 1px solid var(--stk-border);
  padding: 1em;
}

.ask-disclaimer {
  font-size: 0.75em;
  color: var(--stk-text-normal);
}

.list {
  display: flex;
  font-size: 0.875em;
  flex-direction: column;
  gap: 0.5em;
  color: var(--stk-text-normal);
}
</style>
